import { useCallback, useContext } from 'react'
import {
  columnCheckbox,
  withFilterParams,
} from '../../../components/AgGrid/columnDefs'
import {
  AppointmentHistoryGridQuery,
  CountAppointmentHistoryQuery,
} from '../queries'
import {
  appointmentAccessReasonValueGetter,
  appointmentStatusLabelValueGetter,
  appointmentTypeValueGetter,
  dateTimeValueGetter,
  tenantFullNameValueGetter,
} from '../valueGetter'
import UserContext from '../../../helper/userContext'
import { useTranslation } from 'react-i18next'
import { Routes } from '../../../configs/RouteConfig'
import {
  AppointmentAccessRenderer,
  AppointmentIdRenderer,
  AppointmentStatusRenderer,
  AppointmentTypeRenderer,
  TenantFullNameRenderer,
} from '../renderers'
import { DateRenderer } from '../../../components/AgGrid/renderers'
import { getPriorityHeightFromDisplayedColumns } from '../../../helper/calculateRowHeight'
import AppointmentStatusFilter from '../filters/AppointmentStatusFilter'
import AppointmentTypeFilter from '../../Appointments/filters/AppointmentTypeFilter'

export const useAppointmentHistoryGrid = () => {
  const { redirectRoute, currentLanguage } = useContext(UserContext)
  const { t } = useTranslation()
  const getParams = useCallback(
    (currentService, filterVariables) => {
      const { usageUnitId } = filterVariables
      const getRowHeight = (params) => {
        const displayedColumns =
          params.node.columnApi?.getAllDisplayedVirtualColumns()
        const data = params?.data
        const getWidth = (field) =>
          params.node.columnApi?.getColumn?.(field)?.getActualWidth?.()

        const columnParams = [
          {
            field: 'accessReason',
            value: data?.accessReason,
            colWidth: getWidth('accessReason'),
          },
        ]

        return getPriorityHeightFromDisplayedColumns({
          columnParams,
          displayedColumns,
        })
      }
      const valueGetter = (params) => {
        const field = params.column.getColDef().field
        const colId = params.column.getColDef().colId
        const search = colId || field
        switch (search) {
          case 'status':
            return t(appointmentStatusLabelValueGetter(params))
          case 'accessReason':
            return t(appointmentAccessReasonValueGetter(params))
          case 'type':
            return t(appointmentTypeValueGetter(params))
          case 'tenant':
            return tenantFullNameValueGetter(params)
          case 'date':
            return dateTimeValueGetter({
              ...params,
              currentLanguage: currentLanguage,
              formatStr: 'EP',
            })
          case 'executionTime':
            return dateTimeValueGetter({
              ...params,
              currentLanguage: currentLanguage,
              formatStr: 'p',
            })
          case 'executionDate':
            return dateTimeValueGetter({
              ...params,
              currentLanguage: currentLanguage,
              formatStr: 'P',
            })
          default:
            return params.value
        }
      }
      const AppointmentHistoryColumnDefs = [
        columnCheckbox,
        {
          headerName: t('STATUS'),
          field: 'status',
          width: 80,
          filter: 'AppointmentStatusFilter',
          cellRenderer: AppointmentStatusRenderer,
          ...withFilterParams,
        },
        {
          headerName: t('PYREXX PERFORMANCE NUMBER'),
          field: 'tableId',
          width: 70,
          cellRenderer: AppointmentIdRenderer,
          ...withFilterParams,
          type: 'rightAligned',
        },
        {
          headerName: t('ACCESS'),
          field: 'accessReason',
          width: 100,
          filter: 'AppointmentAccessReasonFilter',
          cellRenderer: AppointmentAccessRenderer,
        },
        {
          headerName: t('APPOINTMENT TYPE'),
          field: 'type',
          width: 100,
          filter: 'AppointmentTypeFilter',
          cellRenderer: AppointmentTypeRenderer,
          ...withFilterParams,
        },
        {
          headerName: t('INHABITANT'),
          field: 'tenant',
          width: 100,
          cellRenderer: TenantFullNameRenderer,
          filter: false, // TODO add Tenant filter from UsageUnits->appointments->Tenant
        },
        {
          headerName: t('APPOINTMENT DATE'),
          field: 'date',
          width: 80,
          filter: 'agDateColumnFilter',
          ...withFilterParams,
          sort: 'desc',
          cellRenderer: DateRenderer,
          cellRendererParams: {
            formatStr: 'EP',
          },
        },
        {
          headerName: t('EXECUTED DATE'),
          field: 'executionDate',
          width: 70,
          filter: 'agDateColumnFilter',
          cellRenderer: DateRenderer,
          cellRendererParams: {
            formatStr: 'P',
          },
          ...withFilterParams,
        },
        {
          headerName: t('EXECUTED TIME'),
          colId: 'executionTime', // TODO duplicate ids, filtering and sorting is unavailable
          field: 'executionDate',
          width: 70,
          sortable: false,
          filter: false,
          cellRenderer: DateRenderer,
          cellRendererParams: {
            formatStr: 'p',
          },
        },
        {
          headerName: t('CAUSE OF ORDER'),
          field: 'serviceReason.name',
          width: 100,
          ...withFilterParams,
        },
        {
          headerName: t('INITIATOR'),
          field: 'initiator',
          width: 100,
          ...withFilterParams,
        },
      ]
      const AppointmentsCustomFilter = [
        {
          name: 'contract.serviceCategory.tableId',
          filterType: 'text',
          operator: 'AND',
          conditions: [
            {
              value: [currentService.tableId],
              type: 'equals',
            },
          ],
        },
      ]
      return {
        query: AppointmentHistoryGridQuery,
        columnDefs: AppointmentHistoryColumnDefs,
        customFilters: AppointmentsCustomFilter,
        countQuery: CountAppointmentHistoryQuery,
        extractor: (response) => response.Me?.Company?.UsageUnit?.data,
        customGridOptions: {
          getContextMenuItems: (params) => {
            return [
              {
                // custom item
                name: t('GO TO APPOINTMENT {TABLEID}', {
                  TABLEID: params?.node?.data?.tableId,
                }),
                action: function () {
                  redirectRoute(
                    Routes.appointment + '/' + params?.node?.data?.tableId
                  )
                },
              },
              'copy',
            ]
          },
          getRowHeight,
          components: {
            AppointmentStatusFilter,
            AppointmentTypeFilter,
          },
          processCellForClipboard: valueGetter,
          defaultCsvExportParams: {
            processCellCallback: valueGetter,
          },
        },
        customVariables: { usageUnitId },
      }
    },
    [currentLanguage, redirectRoute, t]
  )
  return { getParams }
}
